import { Component, computed, input } from '@angular/core';
import {
  Currency,
  EmptyClass, EventInfo, getDefaultTimeZoneForCountry, isNullOrUndefined, Price,
  TimeZoneRegion
} from 'in-time-core';
import { LocalizationMixinSignals } from '../../../core/mixins/localization-mixin-signal';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NativePriceFormatter } from '../../../core/utils/native-price-formatter';
import { map, switchMap, ObservableInput, of } from 'rxjs';
import { RouterModule } from '@angular/router';
import { StartDateComponent } from '../start-date/start-date.component';
import { LocationInfoComponent } from '../location-info/location-info.component';
import { PrimaryButtonComponent } from '../primary-button/primary-button.component';
import { TranslocoModule } from '@ngneat/transloco';
import { getActiveAppFlavor } from '../../../app.flavors';

type NativePriceFormatterResult = [string, Currency]

@Component({
  standalone: true,
  selector: 'app-big-event-card',
  templateUrl: './big-event-card.component.html',
  imports: [
    RouterModule, StartDateComponent, LocationInfoComponent,
    PrimaryButtonComponent, TranslocoModule,
  ],
})
export class BigEventCardComponent extends LocalizationMixinSignals(EmptyClass) {
  public readonly event = input.required<EventInfo>();
  public readonly shouldLoadEagerly = input<boolean>();

  public readonly name = computed(() => {
    return this.getLocalizedText(this.event().localizedName) ?? '?';
  });

  public readonly coverImage = computed(() => {
    return this.event().coverImage;
  });

  public readonly timezone = computed(() => {
    const timezoneOfEvent = getDefaultTimeZoneForCountry(this.event().country);

    return timezoneOfEvent ?? TimeZoneRegion.EuropeBudapest;
  });

  private readonly soldOutTranslation = toSignal(
    this.getLocalization$('eventProfile.eventProfileView.soldOut'),
  );

  private readonly event$ = toObservable<EventInfo>(this.event);
  private readonly locale$ = toObservable(this.locale);
  private readonly localizedMinPrice = toSignal(
    this.event$
      .pipe(map<EventInfo, Price | null>((event) => event.pricingInfo?.minPrice ?? null))
      .pipe(switchMap<Price | null, ObservableInput<NativePriceFormatterResult | null>>(
        (minPrice) => minPrice == null
          ? of(null)
          : this.locale$.pipe(map((locale) => [new NativePriceFormatter(locale).format(minPrice), minPrice.currency]))))
      .pipe(switchMap<NativePriceFormatterResult | null, ObservableInput<string | null>>(
        (result) => result == null
          ? of(null)
          : result[1] == Currency.RON
            ? this.getLocalizationOrFallback$('eventCard.minPriceRon', 'eventCard.minPrice', { minPrice: result[0] })
            : this.getLocalization$('eventCard.minPrice', { minPrice: result[0] })))
  );

  public readonly pricing = computed(() => {
    const pricingInfo = this.event().pricingInfo;
    if(isNullOrUndefined(pricingInfo)) {
      return null;
    }

    if(pricingInfo.isSoldOut) {
      return this.soldOutTranslation();
    }

    const minPrice = pricingInfo.minPrice;
    if(isNullOrUndefined(minPrice)) {
      return null;
    }

    const formatter = new NativePriceFormatter(this.locale());
    const maxPrice = pricingInfo.maxPrice;
    if(isNullOrUndefined(maxPrice) || minPrice.isSame(maxPrice)) {
      return this.localizedMinPrice() ?? null;
    }

    return formatter.formatRange(minPrice, maxPrice);
  });

  public readonly eventWebRoute = computed(() => {
    const flavor = getActiveAppFlavor();
    return `${flavor.eventRoutePrefix}${this.event().webRoute}`;
  });
}
